import Check from "@mui/icons-material/Check";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { Box, Button, Paper, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { GridRenderCellParams, useGridApiRef } from "@mui/x-data-grid-premium";
import { mdHeight, SubNavAppBar, xsHeight } from "components/AppBar";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { tabsOffset } from "components/TabLinks";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import {
  CorrectionManifestStatus,
  ExtractionStatus,
  ProcessedManifestsQuery,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags, useStoredFeatureFlags } from "hooks/useFeatureFlags";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useQueryParams } from "hooks/useQueryParams";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { DateTime } from "luxon";
import { OmnisearchProvider } from "providers/OmnisearchProvider";
import { useCallback, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DOCUMENT_SOURCE_LABELS, documentSourceKind } from "routes/Staff/ManifestDocuments/helpers";
import { ManifestDetailDialog } from "routes/Staff/WasteFulfillment/ManifestReview/ManifestDetailDialog";
import { stateOptionLabel } from "util/filterOptionLabels";

import { YearAutocompletePicker } from "../ManifestLineItems/YearAutocompletePIcker";
import { AddManifestButton } from "./AddManifestButton";
import { PROCESSED_MANIFESTS_QUERY } from "./api";
import { CorrectionStatusChip } from "./CorrectionStatusChip";
import { DiscrepanciesCell } from "./DiscrepanciesCell";
import { DiscrepancyReviewDialog } from "./DiscrepancyReviewDialog";
import { DocumentReviewStatusCell } from "./DocumentReviewStatusCell";
import { DOCUMENT_STATE_LABELS, DOCUMENT_STATES, type DocumentState } from "./documentStateDisplay";
import { EManifestCell } from "./EManifestCell";
import { ManifestDetails } from "./ManifestDetails";
import { buildManifestDrawerPath } from "./manifestDrawerPath";
import { ManifestsExportMenu } from "./ManifestsExportMenu";
import { ManifestShowFilterPicker } from "./ManifestShowFilterPicker";
import { SOURCE_LABEL_BY_VALUE, SOURCE_ORDER } from "./manifestSourceDisplay";
import { omnisearchHasCorrectionDateTerm } from "./mergeDefaultAndOmnisearch";
import { reconciliationColumn } from "./reconciliationColumn";
import { SourcesCell } from "./SourcesCell";
import { TransitCell } from "./TransitCell";
import { VendorDashboard } from "./VendorDashboard/VendorDashboard";
import { VendorDashboardToggle } from "./VendorDashboard/VendorDashboardToggle";

export type Row = ProcessedManifestsQuery["processedManifests"]["items"][number];

/**
 * Descriptive document status shown inline in the Sources tooltip's Document
 * line (e.g. "Document: reviewed"). `null` means there's no extra detail to
 * show, so the tooltip falls back to plain present/missing.
 */
const DOCUMENT_STATE_SOURCE_HOVER: Record<DocumentState, string | null> = {
  needsReview: "needs review",
  failed: "import failed",
  notProcessed: "not processed",
  processing: "processing",
  approved: "reviewed",
  none: null,
};

/**
 * "API (RCRAInfo)" / "Human upload" for the source document, or "—" when
 * there is none.
 */
function getDocumentSourceLabel(row: Row): string {
  if (!row.sourceDocument) return "—";
  return DOCUMENT_SOURCE_LABELS[
    documentSourceKind(row.sourceDocument.documentType, row.sourceDocument.originalDocumentType)
  ];
}

function getDocumentState(row: Row): DocumentState {
  const sourceDocument = row.sourceDocument;
  if (!sourceDocument) return "none";
  if (sourceDocument.extractionStatus === ExtractionStatus.Failed) return "failed";
  const metadata = sourceDocument.documentMetadata;
  if (metadata?.needsReview) return "needsReview";
  // "approved" (reviewed) requires an actual human review, not just a
  // successful extraction — match the canonical isManifestDocumentReviewed rule
  // (needsReview === false AND reviewedAt != null).
  if (metadata?.needsReview === false && metadata.reviewedAt != null) return "approved";
  // Extraction in flight, or done and awaiting metadata promotion — mirrors
  // the DocumentsSection Process-gate reading of PENDING/SUCCEEDED.
  if (
    sourceDocument.extractionStatus === ExtractionStatus.Pending ||
    sourceDocument.extractionStatus === ExtractionStatus.Succeeded
  ) {
    return "processing";
  }
  // NOT_ATTEMPTED (the schema default) or null: nothing has been attempted.
  return "notProcessed";
}

/**
 * Manifests list page.
 *
 * Top-level wrapper handles the page chrome (year + Show picker, breadcrumbs) and
 * hoists state into `OmnisearchProvider` so the stats cards and grid share
 * omnisearch state.
 *
 * The actual layout (chips, cards, grid) lives in `ManifestsContent` so the
 * provider can supply omnisearch state to all of its children via context.
 */
export function Manifests() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const persistenceKey = "waste-manifests";
  const theme = useTheme();
  const { featureFlags } = useFeatureFlags();
  const { featureFlags: storedFlags } = useStoredFeatureFlags();
  const { isStaff } = useCurrentUser();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Circles are visible to anyone whose tenant has the add-on; staff-only bulk
  // management (checkbox select, Review Discrepancies, export counts) stays gated.
  const showDiscrepancies = !!featureFlags[FeatureFlagKey.WasteModule];
  const showStaffDiscrepancyControls = isStaff && showDiscrepancies;
  // This grid is not itself flag-gated (unlike the container-tracking routes), so
  // the reconciliation column has to be gated here or it would show an always-
  // empty column to every waste tenant.
  const showContainerTracking = !!featureFlags[FeatureFlagKey.ContainerTracking];
  // Stored waste-module (not the staff-overridden view) — the source-document
  // surfaces only work where the backend actually ingests manifests.
  const showSourceDocument = isStaff && !!storedFlags[FeatureFlagKey.WasteModule];

  const [shippedDateYear, setShippedDateYear] = useLocalStorageState<number>(
    "waste-manifest-shipped-date-year",
    {
      defaultValue: DateTime.now().year,
    }
  );

  // The shipped-year default is the year picker's hidden scope. It yields when
  // the active search carries a correction/raised-date term (the vendor
  // dashboard's chart drilldowns): those charts scope by request/raised date,
  // so pinning shippedDate too would silently drop cross-year manifests the
  // chart counted — and surfacing a shippedDate override in the visible input
  // is noise. The date term takes over as the grid's time scope instead.
  const { filters } = useQueryParams<{ omnisearch?: string }>();
  const activeOmnisearch = filters.omnisearch ?? "";
  const defaultSearch = useMemo(
    () =>
      omnisearchHasCorrectionDateTerm(activeOmnisearch)
        ? `tenantId:${tenantId}`
        : `tenantId:${tenantId} shippedDate:${shippedDateYear}`,
    [tenantId, shippedDateYear, activeOmnisearch]
  );

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Manifests",
    },
  ]);

  const { selectedId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("processedManifestId");

  const navigate = useNavigate();
  const location = useLocation();
  // Latest location kept in a ref so the click handler's identity doesn't
  // churn on every omnisearch keystroke (location.search / pathname change
  // constantly and this callback feeds the columns useMemo).
  const locationRef = useRef({ pathname: location.pathname, search: location.search, selectedId });
  locationRef.current = { pathname: location.pathname, search: location.search, selectedId };
  // Opens the drawer for `row` with a one-shot ?section=documents param so the
  // drawer scrolls to the Documents section (where Process / upload live). One
  // navigation on purpose: pushIdToUrl + setSearchParams in the same tick would
  // race (each reads a stale location). buildManifestDrawerPath truncates any
  // current selection defensively, so a doubled-id path is never possible.
  const handleDocumentReviewClick = useCallback(
    (row: Row) => {
      navigate(buildManifestDrawerPath({ ...locationRef.current, manifestId: row.id }), {
        preventScrollReset: true,
      });
    },
    [navigate]
  );

  const [discrepancyRow, setDiscrepancyRow] = useState<Row | null>(null);

  const columns = useManifestColumns({
    showDiscrepancies,
    showSourceDocument,
    showContainerTracking,
    onDiscrepancyClick: setDiscrepancyRow,
    isStaff,
    onDocumentReviewClick: handleDocumentReviewClick,
  });

  return (
    <Box>
      <OmnisearchProvider
        persistenceKey={persistenceKey}
        columns={columns}
        // These MUST live on the provider, not the inner OmnisearchDataGrid: when a
        // grid is wrapped in an OmnisearchProvider, OmnisearchDataDisplay takes its
        // filter list from the provider context and ignores the grid's own
        // additionalFilterColumns / excludeFilterColumns props. Putting them here is
        // what actually suppresses the no-op `wasteLineCount` ("Line Items") filter
        // and surfaces the State/Territory filter (ENG-15798).
        excludeFilters={["wasteLineCount"]}
        additionalFilters={[
          {
            header: "State/Territory",
            key: "states",
            filterKeyType: "enumAutocomplete",
            enumValues: Object.keys(STATE_NAMES),
            enumPresentationFunction: stateOptionLabel,
          },
          // Both boolean filters ride the single omnisearch string; the
          // data-access omnisearch manager owns the where-fragments. The
          // Discrepancy filter is visible to any WasteModule tenant, but the
          // backend scopes its match to what the caller can see (customers get
          // pending/resolved field discrepancies only, staff get the full open
          // view). EPA corrections are internal staff-review state with no
          // customer-facing surface on this list, so that filter is staff-only.
          ...(showDiscrepancies
            ? [
                {
                  header: "Discrepancy",
                  key: "hasDiscrepancy",
                  filterKeyType: "boolean" as const,
                },
              ]
            : []),
          ...(showStaffDiscrepancyControls
            ? [
                {
                  header: "EPA Correction Request",
                  key: "hasEpaCorrection",
                  filterKeyType: "boolean" as const,
                },
              ]
            : []),
        ]}
        isURLDriven
        initialSortModel={[{ field: "shippedDate", sort: "desc" as const }]}
        initialColumnVisibilityModel={{
          isCertified: false,
          hasSource: false,
          // Document status now lives in the Sources tooltip; keep the column
          // available in column management but hidden by default.
          sourceDocumentState: false,
          // Opt-in diagnostic column (ENG-16032); hidden until a user asks for it.
          documentSource: false,
          // EPA correction status: keep the column available in column management
          // and its enum filter in FILTERS, but hidden from the default view.
          latestEpaCorrectionManifestStatus: false,
          // The Transit column rolls these three up; keep them available in
          // column management / FILTERS but hide them from the default view.
          shippedDate: false,
          receivedDate: false,
          eManifestDaysInTransit: false,
          ...(isMobile && {
            epaId: false,
            wasteLineCount: false,
            vendor: false,
          }),
        }}
      >
        <SubNavAppBar
          sx={{
            py: 2,
            px: 3,
            borderBottom: "1px solid",
            borderColor: theme.palette.divider,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ width: 80 }}>
                <YearAutocompletePicker
                  label="Year"
                  minYear={2018}
                  value={shippedDateYear}
                  onChange={setShippedDateYear}
                />
              </Box>
              <ManifestShowFilterPicker defaultSearch={defaultSearch} />
            </Stack>
            <VendorDashboardToggle />
          </Stack>
        </SubNavAppBar>
        <VendorDashboard shippedDateYear={shippedDateYear} />
        <ManifestsContent
          tenantId={tenantId ?? ""}
          defaultSearch={defaultSearch}
          persistenceKey={persistenceKey}
          shippedDateYear={shippedDateYear}
          columns={columns}
          showStaffDiscrepancyControls={showStaffDiscrepancyControls}
          showManifestPdfExport={isStaff}
          pushIdToUrl={pushIdToUrl}
        />
      </OmnisearchProvider>
      {/*
        The detail drawer (and its line-items grid) renders OUTSIDE the
        OmnisearchProvider on purpose. OmnisearchDataGrid reuses any ancestor
        OmnisearchProvider's grid state, so nesting the drawer inside would make
        the line-items grid inherit the manifests grid's sort/columns (e.g.
        `sourceDocumentState`) and crash the `wasteLinesUnified` query. Keeping
        it a sibling gives the line-items grid its own independent state.
      */}
      <ManifestDetails
        processedManifestId={selectedId}
        open={!!selectedId}
        onClose={popIdFromUrl}
      />
      {/*
        Gated by the same flag as the circles: without the waste-module, the
        discrepancy column never renders, so this modal can never be opened —
        keeping it behind `showDiscrepancies` ensures none of the discrepancy UI
        mounts at all when the tenant lacks the waste-module.
      */}
      {showDiscrepancies && (
        <ManifestDetailDialog
          mode="customer"
          // Customers see only resolved discrepancies in the modal; a staff viewer
          // on this customer grid keeps the open (active) view (ENG-15854).
          customerView={!isStaff}
          open={discrepancyRow !== null}
          onClose={() => setDiscrepancyRow(null)}
          processedManifestId={discrepancyRow?.id ?? null}
          manifestTrackingNumber={discrepancyRow?.manifestTrackingNumber ?? null}
          facilityName={discrepancyRow?.facility?.name ?? null}
        />
      )}
    </Box>
  );
}

interface ManifestsContentProps {
  tenantId: string;
  defaultSearch: string;
  persistenceKey: string;
  shippedDateYear: number;
  columns: OmnisearchGridColDef<Row>[];
  showStaffDiscrepancyControls: boolean;
  showManifestPdfExport: boolean;
  pushIdToUrl: (id: string) => void;
}

function ManifestsContent({
  tenantId,
  defaultSearch,
  persistenceKey,
  shippedDateYear,
  columns,
  showStaffDiscrepancyControls,
  showManifestPdfExport,
  pushIdToUrl,
}: ManifestsContentProps) {
  const theme = useTheme();
  const greyBackground = theme.palette.canvas;
  const apiRef = useGridApiRef();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showBulkReviewDialog, setShowBulkReviewDialog] = useState(false);

  const filename = useMemo(() => `waste-manifests-${shippedDateYear}`, [shippedDateYear]);

  return (
    <Box
      sx={{
        background: greyBackground,
        // 16px gutters to match DashboardLayout (waste overview et al). The
        // vendor dashboard above carries no bottom padding, so this top
        // padding is the full tile-to-tile gutter.
        p: 2,
        display: "flex",
        flexDirection: "column",
        height: {
          xs: `calc(100vh - ${theme.spacing(xsHeight + tabsOffset)})`,
          md: `calc(100vh - ${theme.spacing(mdHeight + tabsOffset)})`,
        },
        minHeight: 0,
      }}
    >
      <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Grid section — its own white card holding the omnisearch + grid. */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            overflow: "hidden",
            // Breathing room above the omnisearch bar and below the grid footer.
            py: 2,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <OmnisearchDataGrid
            fillParentHeight
            apiRef={apiRef}
            persistenceKey={persistenceKey}
            isURLDriven
            columns={columns}
            dataQuery={PROCESSED_MANIFESTS_QUERY}
            getItems={(data) => data.processedManifests.items}
            getCount={(data) => data.processedManifests.count}
            noDataMessage={`No manifests have been added to this organization.`}
            checkboxSelection={showStaffDiscrepancyControls}
            onSelectedIdsChanged={
              showStaffDiscrepancyControls ? (ids) => setSelectedIds(ids.map(String)) : undefined
            }
            disableRowSelectionOnClick
            onRowClick={({ row: { id } }) => pushIdToUrl(id)}
            defaultSearch={defaultSearch}
            // Compact rows match the rest of Oak; matches the Figma "compact and
            // terse" direction. Auto-height was forgiving but ate vertical space.
            rowHeight={38}
            commandButtons={({ search }) => [
              ...(showStaffDiscrepancyControls && selectedIds.length > 0
                ? [
                    <Button
                      key="review-discrepancies"
                      variant="outlined"
                      color="staff"
                      startIcon={<CompareArrowsIcon />}
                      onClick={() => setShowBulkReviewDialog(true)}
                    >
                      Review Discrepancies ({selectedIds.length})
                    </Button>,
                  ]
                : []),
              <AddManifestButton key="add-manifest" tenantId={tenantId} />,
              <ManifestsExportMenu
                key="export"
                tenantId={tenantId}
                fileName={filename}
                search={search}
                selectedIds={selectedIds}
                shippedDateYear={shippedDateYear}
                showDiscrepancyCounts={showStaffDiscrepancyControls}
                showManifestPdfExport={showManifestPdfExport}
              />,
            ]}
            showFilters
            // NOTE: additionalFilterColumns / excludeFilterColumns are intentionally
            // NOT set here — this grid is wrapped in an OmnisearchProvider, which owns
            // the filter list. They live on the provider above (ENG-15798).
          />
        </Paper>
        {showBulkReviewDialog && selectedIds.length > 0 && (
          <DiscrepancyReviewDialog
            processedManifestIds={selectedIds}
            onClose={() => setShowBulkReviewDialog(false)}
          />
        )}
      </Stack>
    </Box>
  );
}

export interface UseManifestColumnsArgs {
  showDiscrepancies: boolean;
  showSourceDocument: boolean;
  /** Container-tracking tenants get the reconciliation column; nobody else. */
  showContainerTracking: boolean;
  onDiscrepancyClick?: (row: Row) => void;
  /**
   * Whether the viewer is staff. A customer reading discrepancies for a
   * facility-less manifest is denied by the `manifestDiscrepancies` authorizer,
   * so the circle is only made clickable for staff or rows that have a facility.
   */
  isStaff: boolean;
  onDocumentReviewClick?: (row: Row) => void;
}

/**
 * Column definitions for the manifests grid.
 *
 * Splits the table into three readable groups:
 *
 * 1. **Identity**: tracking #, EPA ID, facility.
 * 2. **Provenance + lifecycle** — two cleanly separated deltas plus source
 *    availability: Sources (3-pill availability indicator; tooltip lists each
 *    source and the days-awaiting count on a missing e-Manifest), Transit
 *    (two-line Shipped / Received + transit days, rolling up the hidden ship
 *    date / received date / days-in-transit columns), and e-Manifest (the
 *    awaiting-days countdown with 45/60-day color + compliance action state;
 *    sorts/filters on the `daysSinceShipment` ordinal).
 * 3. **Filtering-only**: vendor, isCertified, hasSource, document state, plus
 *    the three Transit source columns — these stay around so the FILTERS
 *    popover and column management keep existing audit muscle memory; most are
 *    hidden by default. (e-Manifest presence is shown by Sources and filtered
 *    via `hasSource`; the Show picker's overdue/awaiting presets apply
 *    `isEManifest` as raw omnisearch terms, so no grid column is needed.)
 *
 * Compare Sources is reached from the manifest drawer, not a grid column.
 *
 * Exported so the V3 Manifests grid renders these exact definitions instead of
 * a second set that would drift; the body is unchanged from before this was
 * exported.
 */
export function useManifestColumns({
  showDiscrepancies,
  showSourceDocument,
  showContainerTracking,
  onDiscrepancyClick,
  isStaff,
  onDocumentReviewClick,
}: UseManifestColumnsArgs): OmnisearchGridColDef<Row>[] {
  const minWidth = 120;

  return useMemo<OmnisearchGridColDef<Row>[]>(
    () => [
      {
        field: "manifestTrackingNumber",
        headerName: "Manifest #",
        minWidth,
        flex: 1,
      },
      {
        field: "epaId",
        headerName: "EPA ID",
        minWidth,
        flex: 1,
      },
      {
        field: "facility.name",
        headerName: "Facility",
        filterKeyType: "wasteFacility",
        minWidth,
        flex: 1,
        valueGetter: (_, row) => row.facility?.name ?? "",
      },
      {
        field: "sources",
        headerName: "Sources",
        minWidth: 96,
        width: 96,
        flex: 0,
        sortable: false,
        filterable: false,
        align: "left",
        headerAlign: "left",
        renderCell: ({ row }: GridRenderCellParams<Row>) => (
          <SourcesCell
            availableSources={row.availableSources ?? []}
            // Only uniform-hazardous manifests are actually awaiting a required
            // e-Manifest, so only they get the days count (matches EManifestCell).
            daysAwaitingEManifest={row.isUniformHazardousManifest ? row.daysSinceShipment : null}
            documentStatusLabel={DOCUMENT_STATE_SOURCE_HOVER[getDocumentState(row)]}
          />
        ),
      },
      {
        // Combines Ship Date / Received Date / Days in transit into one
        // two-line cell. The three underlying columns still exist (below) for
        // the FILTERS popover and column management, but are hidden by default.
        field: "transit",
        headerName: "Transit",
        minWidth: 160,
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: ({ row }: GridRenderCellParams<Row>) => <TransitCell input={row} />,
      },
      {
        field: "shippedDate",
        headerName: "Ship Date",
        minWidth,
        flex: 1,
        filterKeyType: "date",
        valueFormatter: (value) => {
          return value ? getUtcDisplayDate(value, "med") : null;
        },
      },
      {
        field: "receivedDate",
        headerName: "Received Date",
        minWidth,
        flex: 1,
        filterKeyType: "date",
        valueFormatter: (value) => (value ? getUtcDisplayDate(value, "med") : null),
      },
      {
        field: "eManifestDaysInTransit",
        headerName: "Days in transit",
        minWidth,
        flex: 1,
        filterKeyType: "number",
      },
      {
        // Single e-Manifest compliance column. Sorts/filters on the raw
        // days-since-shipment ordinal; the cell renders "X days" with the
        // 45/60-day color scale plus the current compliance action state
        // (folding in the retired Status column's transporter-contacted /
        // exception-report signals). Only uniform-hazardous manifests still
        // awaiting their e-Manifest render a value.
        field: "daysSinceShipment",
        headerName: "e-Manifest",
        minWidth: 132,
        flex: 1,
        filterKeyType: "number",
        sortable: true,
        renderCell: ({ row }: GridRenderCellParams<Row>) => <EManifestCell input={row} />,
      },
      {
        field: "wasteLineCount",
        headerName: "Line Items",
        flex: 0.6,
        minWidth: 90,
      },
      ...(showDiscrepancies
        ? [
            {
              field: "discrepancies" as const,
              headerName: "Discrepancies",
              flex: 0.8,
              minWidth: 112,
              sortable: false,
              filterable: false,
              renderCell: ({ row }: GridRenderCellParams<Row>) => (
                <DiscrepanciesCell
                  tally={row.discrepancyTally}
                  // Customers see only resolved discrepancies; staff keep the full
                  // unresolved/pending/resolved + compliance view (ENG-15854).
                  customerView={!isStaff}
                  // A customer can't read discrepancies for a facility-less manifest
                  // (the authorizer denies it), so only make the circle clickable for
                  // staff or rows that have a facility — avoids opening to a 403.
                  onClick={
                    onDiscrepancyClick && (isStaff || row.facility?.id)
                      ? () => onDiscrepancyClick(row)
                      : undefined
                  }
                />
              ),
            },
          ]
        : []),

      ...reconciliationColumn(showContainerTracking),

      {
        // EPA correction status chip (Corrected / Under correction). The
        // `enum` filterKeyType turns the column header into a select filter
        // over the two correction states; a manifest not in correction shows
        // an empty cell.
        field: "latestEpaCorrectionManifestStatus",
        headerName: "Correction Status",
        flex: 0.8,
        minWidth: 130,
        sortable: false,
        enumValues: Object.values(CorrectionManifestStatus),
        filterKeyType: "enum",
        renderCell: ({ row }: GridRenderCellParams<Row>) => (
          <CorrectionStatusChip status={row.latestEpaCorrectionManifestStatus} />
        ),
      },

      // -------- Filtering-only / legacy columns (hidden by default) --------
      {
        field: "vendor",
        headerName: "Vendor",
        flex: 1,
        filterKeyType: "wasteVendor",
      },
      {
        field: "isCertified",
        headerName: "Certified",
        flex: 1,
        sortable: true,
        filterKeyType: "boolean",
        renderCell: ({ row }) => (row.isCertified ? <Check fontSize="small" /> : null),
      },
      {
        field: "hasSource",
        headerName: "Sources",
        flex: 1,
        sortable: false,
        // Filter-only: not column-manageable, so it can't collide with the
        // visible "Sources" pills column (field "sources") in Manage Columns.
        hideable: false,
        // Multi-select with AND/superset semantics — the same sources filter the
        // Staff Manifest Review queue uses. Selecting e-Manifest + Vendor returns
        // manifests that have BOTH on file; a document counts only once reviewed.
        // The backend `hasSource:` handler (buildHasSourceFilter) already ANDs
        // multi-value matches, so this is purely a control swap (enum → multi).
        // Order + labels mirror the E/V/D pills via the shared SOURCE_ORDER.
        filterKeyType: "enumAutocomplete",
        enumValues: [...SOURCE_ORDER],
        enumPresentationFunction: (value: string) => SOURCE_LABEL_BY_VALUE.get(value) ?? value,
        // No cell — this column exists strictly for the filter UI; its
        // visibility is forced off in `initialColumnVisibilityModel` above.
        renderCell: () => null,
      },
      {
        // Display-only: no backend search key for a ProcessedManifest's document
        // provenance, so this can't sort/filter like the staff Manifest Documents
        // page's equivalent "Source" column. Values mirror it exactly (ENG-16032).
        field: "documentSource",
        headerName: "Document Source",
        minWidth,
        flex: 0.8,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => getDocumentSourceLabel(row),
        renderCell: ({ row }: GridRenderCellParams<Row>) => {
          const label = getDocumentSourceLabel(row);
          return (
            <Typography
              variant="body2"
              noWrap
              color={row.sourceDocument ? undefined : "text.secondary"}
            >
              {label}
            </Typography>
          );
        },
      },
      ...(showSourceDocument
        ? [
            {
              field: "sourceDocumentState" as const,
              headerName: "Document Review",
              minWidth: 130,
              flex: 0.5,
              align: "left" as const,
              headerAlign: "left" as const,
              // Not server-sortable: ranking the six derived states needs a
              // per-row correlated subquery that blows the transaction window
              // on large tenants under the mandatory nestloop=off tuning. The
              // column filters fine (denormalized where-clause); only sort is
              // unsafe. Filter to triage instead of sorting.
              sortable: false,
              filterable: true,
              filterKeyType: "enum" as const,
              enumValues: [...DOCUMENT_STATES],
              enumPresentationFunction: (value: string) => {
                const labels: Record<string, string> = DOCUMENT_STATE_LABELS;
                return labels[value] ?? value;
              },
              valueGetter: (_: unknown, row: Row) => getDocumentState(row),
              renderCell: ({ row }: GridRenderCellParams<Row>) => (
                <DocumentReviewStatusCell
                  state={getDocumentState(row)}
                  onClick={onDocumentReviewClick ? () => onDocumentReviewClick(row) : undefined}
                />
              ),
            },
          ]
        : []),
    ],
    [
      showDiscrepancies,
      showSourceDocument,
      showContainerTracking,
      onDiscrepancyClick,
      isStaff,
      onDocumentReviewClick,
    ]
  );
}
